import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { MatDialog } from '@angular/material/dialog';
import { DatePipe } from '@angular/common';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { Store } from '@ngrx/store';
import { finalize, takeUntil, tap } from 'rxjs/operators';

import { ProductTypes } from '../../../products/product-types';
import { TableColumnModel } from '../../../../models/table-column.model';
import { generatePositionsCardsInfo, topBlockControls } from 'projects/workspace/src/app/outgoing-invoice/pages/positions/positions.config';
import { InvoiceApiService } from 'projects/workspace/src/app/outgoing-invoice/services/invoice-api.service';
import { UIStatesEnum } from '../../../../models';
import { OutgoingInvoiceModel, OutgoingInvoicePositionModel, PositionsModel } from 'projects/workspace/src/app/outgoing-invoice/models';
import { AppState } from 'projects/workspace/src/app/store/state/app.state';
import {
  CommonModalsActionsEnum,
  ConfirmModalComponent,
  DangerModalComponent,
  PositionGroupModalComponent,
  WarningModalComponent
} from '../../modals-common';
import {
  ControlTypeEnum,
  InputDropdownTypeEnum,
  PositionInfoListModel,
  PositionItemTypeEnum
} from '../../../position-card/position-card-models.model';
import { HighlightTypeEnum } from '../../../ui-components/table-summary-bar/table-summary-bar.model';
import {
  CHANGE_CORRECTION_CALCULATION_MODAL_DATA,
  CHANGE_PARENT_QUANTITY_MODAL_DATA,
  REMOVE_POSITION_MODAL_DATA,
  REMOVE_POSITION_NECESSARILY_MODAL_DATA,
  REMOVE_POSITION_PARENT_MODAL_DATA
} from '../../modals.contsans';
import {
  selectOutgoingInvoiceCurrentState,
  selectOutgoingInvoicePositions,
  selectOutgoingInvoicePositionsGroups
} from 'projects/workspace/src/app/outgoing-invoice/store/selectors';
import { OinTypeEnum, OutgoingInvoiceListTabsEnum } from 'projects/workspace/src/app/outgoing-invoice/enums';
import { PositionGroupModel, ProductUnitModel } from 'projects/workspace/src/app/shared/models';
import { PositionGroupsService, ProductUnitApiService } from 'projects/workspace/src/app/shared/services';
import { PositionsActionsType } from '../../../ui-components/table/custom-table.enums';
import { PositionGroupingEnum, PositionMediaTypeEnum, PriceOptionEnum } from 'projects/workspace/src/app/shared/enums';
import { selectReportingSettings } from 'projects/workspace/src/app/store/selectors/shared.selectors';
import { ReportingPeriodsPreferencesModel } from 'projects/workspace/src/app/reporting-periods/models';
import { PositionMediaFormComponent } from 'projects/workspace/src/app/shared/forms/position-media-form/position-media-form.component';
import { ARBITRARY_POSITION_TYPES_LIST } from 'projects/workspace/src/app/accounting/accounting.constants';
import { selectCompanyProfile } from 'projects/workspace/src/app/administration/store/selectors';
import { CompanyProfile } from 'projects/workspace/src/app/administration/models/company-profile.model';
import { get } from 'lodash';

@Component({
  selector: 'rnpl-outgoing-invoice-edit-position-modal',
  templateUrl: './outgoing-invoice-edit-position-modal.component.html',
  styleUrls: ['./outgoing-invoice-edit-position-modal.component.scss']
})
export class OutgoingInvoiceEditPositionModalComponent implements OnInit, OnDestroy {

  public activeTab: 'OVERVIEW'|'BUNDLE_PRODUCTS' = 'OVERVIEW';
  public tabs = [
    {
      tabName: 'OVERVIEW',
      label: 'APP.POSITION'
    },
    {
      tabName: 'BUNDLE_PRODUCTS',
      label: 'BUNDLES.PRODUCTS_IN_BUNDLE'
    }
  ];

  public companyProfile: CompanyProfile;
  public reportingPeriodPreferences: ReportingPeriodsPreferencesModel;

  public positionsCardInfoByTypes: {[key in ProductTypes]?: any[]} = {
    [ProductTypes.GOODS]: [] as TableColumnModel[],
    [ProductTypes.SERVICES]: [] as TableColumnModel[],
    [ProductTypes.DIGITAL]: [] as TableColumnModel[],
    [ProductTypes.BUNDLE]: [] as TableColumnModel[],
    [ProductTypes.ARBITRARY]: [] as TableColumnModel[],
    [ProductTypes.CORRECTION]: [] as TableColumnModel[],
    [ProductTypes.OVERDUE_FEE]: [] as TableColumnModel[],
  };
  public topBlockControls: {[key in ProductTypes]?: any[]} = {...this.positionsCardInfoByTypes};

  public currentState: UIStatesEnum;
  public UIStates: typeof UIStatesEnum = UIStatesEnum;
  public controlTypeEnum: typeof ControlTypeEnum = ControlTypeEnum;
  public positionItemTypeEnum: typeof PositionItemTypeEnum = PositionItemTypeEnum;
  public inputDropdownTypeEnum = InputDropdownTypeEnum;
  public positionGroupingEnum = PositionGroupingEnum;
  public mediaTypeEnum = PositionMediaTypeEnum;
  public position: OutgoingInvoicePositionModel;
  public allPositions: OutgoingInvoicePositionModel[];
  public groupPositions: OutgoingInvoicePositionModel[];
  public positionsByGroup: PositionGroupModel[];
  public rowIndex: number;
  public positionsByProductType: {[key in ProductTypes]?: any[]};

  public arbitraryPositionTypesList = ARBITRARY_POSITION_TYPES_LIST;
  public unitsList: string[] = [];
  public unitsListSource: ProductUnitModel[] = [];
  public isReadonly: boolean = true;
  public showControls: boolean = true;
  public editableFinalInvoicePosition: boolean = false;

  // public modalNameEnum: typeof ModalNameEnum = ModalNameEnum;

  @ViewChild('positionMediaFormComponent', { static: false }) positionMediaFormComponentRef: PositionMediaFormComponent;

  public positionsUpdateRequest$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    public dialogRef: MatDialogRef<OutgoingInvoiceEditPositionModalComponent>,
    private readonly dialog: MatDialog,
    private readonly invoiceApiService: InvoiceApiService,
    private readonly positionGroupsService: PositionGroupsService,
    private readonly store: Store<AppState>,
    private readonly datePipe: DatePipe,
    private productUnitApiService: ProductUnitApiService,
    @Inject(MAT_DIALOG_DATA) public data: {
      currentInvoice: OutgoingInvoiceModel;
      position: OutgoingInvoicePositionModel,
      hasLinkedOFR: boolean,
      groups: PositionGroupModel[],
    }
  ) {
    this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.dialogRef.close();
      }
    });

    this.store.select(selectCompanyProfile)
      .pipe(takeUntil(this.destroy$))
      .subscribe((companyProfile: CompanyProfile) => this.companyProfile = companyProfile);
  }

  ngOnInit() {
    this.isLoading$.next(true);
    this.position = this.preparePosition(this.data.position);
    // this.positions = this.data.positions;
    // this.positionsByProductType = this.data.positionsByProductType;
    // this.rowIndex = this.getRowIndex(this.position);

    this.store.select(selectReportingSettings)
      .pipe(takeUntil(this.destroy$))
      .subscribe((preferences: ReportingPeriodsPreferencesModel) => {
        this.reportingPeriodPreferences = preferences;
      });

    this.trackInvoiceStateChanges();
    this.trackPositionsChanges();
    this.trackPositionsGroupsChanges();
    this.getPositionsCards();
    this.getProductUnits();
  }

  public getProductUnits(): void {
    this.productUnitApiService.getUnits$(this.position.productType)
      .pipe(takeUntil(this.destroy$))
      .subscribe((unitsList: ProductUnitModel[]) => {
        this.unitsListSource = unitsList;
        if (!this.position.general && !this.isArbitraryProductType) {
          this.unitsList = [this.position.unitType];
          return;
        } else {
          this.unitsList = unitsList.map(u => u.name);
        }
        this.redrawControls();
      });
  }

  private trackPositionsChanges(): void {
    if (this.data.currentInvoice.positionGroupingOption !== PositionGroupingEnum.PRODUCT_TYPE) { return; }

    this.positionsUpdateRequest$.next(true);

    this.store.select(selectOutgoingInvoicePositions)
      .pipe(
        finalize(() => this.positionsUpdateRequest$.next(false)),
        takeUntil(this.destroy$)
      )
      .subscribe((response: PositionsModel) => {
        if (response) {
          this.positionsByProductType = response;
          this.setupPositionsAndGroups();
        }
      });
  }

  private trackPositionsGroupsChanges(): void {
    if (this.data.currentInvoice.positionGroupingOption !== PositionGroupingEnum.USER_DEFINED) { return; }

    this.positionsUpdateRequest$.next(true);

    this.store.select(selectOutgoingInvoicePositionsGroups)
      .pipe(
        finalize(() => this.positionsUpdateRequest$.next(false)),
        takeUntil(this.destroy$)
      )
      .subscribe((positionsGroups: PositionGroupModel[]) => {
        this.positionsByGroup = positionsGroups;
        this.setupPositionsAndGroups();
      });
  }

  public setupPositionsAndGroups(): void {
    if (this.data.currentInvoice.positionGroupingOption === PositionGroupingEnum.PRODUCT_TYPE) {
      this.groupPositions = this.positionsByProductType[this.position.productType];
      this.allPositions = [].concat(Object.values(this.positionsByProductType));
    }
    if (this.data.currentInvoice.positionGroupingOption === PositionGroupingEnum.USER_DEFINED) {
      this.groupPositions = this.positionsByGroup
        .find(g => +g.groupId === this.position.groupId)
        .positions;
      this.allPositions = [].concat(...this.positionsByGroup.map(item => item.positions));
    }

    this.rowIndex = this.getRowIndex(this.position);
  }

  private trackInvoiceStateChanges(): void {
    this.store.select(selectOutgoingInvoiceCurrentState)
      .pipe(takeUntil(this.destroy$))
      .subscribe((state: UIStatesEnum) => {
        this.isReadonly = state === UIStatesEnum.VIEW;
        if (
          this.data.currentInvoice.status === OutgoingInvoiceListTabsEnum.PAID ||
          this.data.currentInvoice.status === OutgoingInvoiceListTabsEnum.DELETED ||
          this.data.currentInvoice.status === OutgoingInvoiceListTabsEnum.CANCELED
        ) {
          this.isReadonly = true;
        }
      });

  }

  private getCurrentInvoice(): void {
    this.invoiceApiService.getOutgoingInvoiceById(this.data.currentInvoice.invoiceId)
      // .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  private getPositionsCards(): void {
    this.isLoading$.next(false);
    if (this.data.currentInvoice) {
       this.editableFinalInvoicePosition = this.isFinalInvoice &&
        (this.isChannelOffers &&
          !this.position.oinPositionId
          && (this.position.productTypeForGrouping === ProductTypes.GOODS ||
            this.position.productTypeForGrouping === ProductTypes.SERVICES ||
            this.position.productTypeForGrouping === ProductTypes.DIGITAL ||
            this.position.productTypeForGrouping === ProductTypes.CORRECTION
          )
        );
      this.positionsCardInfoByTypes[this.position.productTypeForGrouping] = generatePositionsCardsInfo(
        this.position.productType || this.position.productTypeForGrouping,
        this.position.productTypeForGrouping,
        this.data.currentInvoice.billingData.vatDisabled || this.data.currentInvoice.billingData.smallBusiness,
        this.isFinalInvoice,
        this.isRegularInvoice,
        this.position.correctionType,
        this.editableFinalInvoicePosition,
        this.data.currentInvoice.preferences.priceOption,
        this.data.currentInvoice.billingData.smallBusiness,
        get(this.data.currentInvoice, 'partnerInfo.legalCountry'),
      );

      this.topBlockControls[this.position.productTypeForGrouping] = topBlockControls(
        this.position.productType || this.position.productTypeForGrouping,
        this.data.currentInvoice.billingData.vatDisabled || this.data.currentInvoice.billingData.smallBusiness,
        this.isFinalInvoice,
        this.isRegularInvoice,
        this.editableFinalInvoicePosition,
        this.data.currentInvoice.preferences.priceOption,
        this.data.currentInvoice.billingData.smallBusiness,
      );
    }
  }

  private movePosition(posId: number, moveTo: number): void {
    this.invoiceApiService.changePositionOrder(posId, moveTo, +this.data.currentInvoice.invoiceId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        data[this.position.productType].map(pos => {
          if (pos.productId === this.position.productId) {
            this.position = pos;
          }
        });

        this.getCurrentInvoice();
      });
  }

  private deletePositions(ids: number[]): void {
    this.invoiceApiService.deleteOutgoingInvoicePositions(this.data.currentInvoice.invoiceId, ids, this.data.hasLinkedOFR)
      .pipe(takeUntil(this.destroy$))
      .subscribe(()  => {
        this.getCurrentInvoice();
        this.dialogRef.close();
      });
  }

  public actionListHandler({actionType, row, moveTo, returnData}) {
    if (actionType === PositionsActionsType.TO_GROUP) {
      if (!returnData) {
        // create group and move position to it
        this.dialog.open(PositionGroupModalComponent, {
          data: {
            submitRequest$: (groupName: string) => {
              return this.positionGroupsService.createOINGroup(groupName, this.data.currentInvoice.invoiceId)
                .pipe(tap((groups: PositionGroupModel[]) => {
                  const createdGroup = groups.find(g => g.name === groupName);
                  this.movePositionToGroup(createdGroup, row);
                }));
            }
          },
        });
        return;
      }
      // move to existed group
      this.movePositionToGroup(returnData, row);
      return;
    }
    this.movePosition(row.positionId, moveTo);
  }

  public movePositionToGroup(group: PositionGroupModel, position: OutgoingInvoicePositionModel): void {
    this.positionGroupsService.moveOINPositionToGroup(
      this.data.currentInvoice.invoiceId, group.groupId, {ids: [position.positionId]}
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  public getRowIndex(position): number {
    let rowIndex: number;
    this.groupPositions.map((pos, index) => {
      if (pos.positionId === position.positionId) {
        rowIndex = index;
      }
    });
    return rowIndex;
  }

  public deletePositionDialog(): void {
    let data = REMOVE_POSITION_MODAL_DATA;

    if (this.position.linked && this.position.linked.related) {
      data = REMOVE_POSITION_PARENT_MODAL_DATA;
    }

    if (this.position.necessarily) {
      data = REMOVE_POSITION_NECESSARILY_MODAL_DATA;
    }

    const dialog = this.dialog.open(DangerModalComponent, {
      data
    });

    dialog.afterClosed().subscribe((res) => {
      if (res === CommonModalsActionsEnum.CONFIRM) {
        this.deletePositions([this.position.positionId]);
      }
    });
  }

  public trackByProp(index: number, item: PositionInfoListModel) {
    return item.prop;
  }

  public updatePosition(cell: string, value: string|number, positionId = this.position.positionId,  force: boolean = false): void {
    // correction position soft validation
    if (cell === 'specifyAmountsSeparately' && !value && !force) {
      if (
        (this.position.netTotalPrice && (this.position.netTotalPrice !== '0.00')) ||
        (this.position.grossTotalPrice && (this.position.grossTotalPrice !== '0.00')) ||
        (this.position.vatTotal && (this.position.vatTotal !== 0))
      ) {
        const dialog = this.dialog.open(WarningModalComponent, {
          data: CHANGE_CORRECTION_CALCULATION_MODAL_DATA
        });

        dialog.afterClosed().subscribe(res => {
          if (res === CommonModalsActionsEnum.CONFIRM) {
            this.updatePosition(cell, value, positionId, true);
          } else {
            this.getCurrentInvoice();
          }
        });
        return;
      }
    }

    if (cell === 'quantity' && !force && this.position.linked && this.position.linked.related) {
      this.dialog.open(ConfirmModalComponent, {
        data: CHANGE_PARENT_QUANTITY_MODAL_DATA,
      }).afterClosed()
        .subscribe((res: CommonModalsActionsEnum) => {
          if (res === CommonModalsActionsEnum.CONFIRM) {
            this.updatePosition(cell, value, positionId, true);
          }
        });
      return;
    }

    this.patchPosition(cell, value, positionId);
  }

  public updateBundleProduct(cell: string, value: string|number, bundleProductId: number): void {
    this.invoiceApiService.updateOutgoingInvoiceBundledProduct(
      this.data.currentInvoice.invoiceId,
      this.position.positionId,
      bundleProductId,
      cell,
      value
    )
      .subscribe((position: OutgoingInvoicePositionModel) => {
        this.position = this.preparePosition(position);
        this.getCurrentInvoice();
      });
  }

  public patchPosition(fieldName, fieldValue, positionId = this.position.positionId): void {
    this.invoiceApiService.updateOutgoingInvoicePosition(
      this.data.currentInvoice.invoiceId,
      {
        fieldName,
        fieldValue,
        positionId
      }
    )
      // .pipe(takeUntil(this.destroy$))
      .subscribe((response: OutgoingInvoicePositionModel) => {
        if (this.position.positionId === positionId) {
          this.position = this.preparePosition(response);
          this.getPositionsCards();
          if (fieldName === 'productType') {
            this.getProductUnits();
          }
        }
        this.getCurrentInvoice();
      }, () => {
        if (fieldName === 'unitType') {
          this.redrawControls();
        }
      });
  }

  public deleteMediaPositionFile(positionId: number): void {
    this.invoiceApiService.deleteMediaPositionFile(this.data.currentInvoice.invoiceId, positionId)
      .subscribe((positions: PositionsModel) => {
        if (positions['media'] && positions['media'].length) {
          this.position = positions['media'].find(p => p.positionId === this.position.positionId);
          this.position = this.preparePosition(this.position);
        }
        this.getCurrentInvoice();
      }, () => {
        this.position = {...this.position};
      });
  }

  public updateMediaPositionFile(positionId: number, event: { file: File; width: number; height: number }): void {
    this.invoiceApiService.updateMediaPositionFile(this.data.currentInvoice.invoiceId, positionId, event.file, event.width, event.height)
      .subscribe((positions: PositionsModel) => {
        if (positions['media'] && positions['media'].length) {
          this.position = positions['media'].find(p => p.positionId === this.position.positionId);
          this.position = this.preparePosition(this.position);
        }
        // this.getPositionsCards();
        this.getCurrentInvoice();
      }, () => {
        this.position = {...this.position};
        this.positionMediaFormComponentRef.resetImg();
        this.positionMediaFormComponentRef.resetImgInput();
      });
  }

  public redrawControls(): void {
    this.showControls = false;
    setTimeout(() => this.showControls = true);
  }

  public updateDatePicker(event, fieldName): void {
    this.updatePosition(fieldName, event.value ? new Date(event.value).toLocaleDateString('fr-CA') : null);
  }

  private preparePosition(position: OutgoingInvoicePositionModel): OutgoingInvoicePositionModel {
    const negativeCorrection = position.productType === ProductTypes.CORRECTION &&
      position.productTypeForGrouping === ProductTypes.CORRECTION &&
      position.correctionType === 'NEGATIVE';

    return {
      ...position,
      productName: position.description,
      // productNameServices: position.description,
      productLink: {
        label: position.runpleId,
        routerLink: position.positionType === 'REGULAR'
          ? `/products/product-view/${position.productId}`
          : null
      },
      productNameLink: {
        label: position.description,
        routerLink: position.positionType === 'REGULAR'
          ? `/products/product-view/${position.productId}`
          : null
      },
      providedWithin: this.getProvidedWithinValue(position),
      currencyNegativeRow: negativeCorrection || position.productType === ProductTypes.CREDIT,
      ['customHighlight']: {
        [HighlightTypeEnum.NEGATIVE_TO_RED]: {
          netTotalPrice: negativeCorrection,
          vatTotal: negativeCorrection,
          grossUnitPrice: negativeCorrection,
        }
      },
    };
  }

  private getProvidedWithinValue(pos: OutgoingInvoicePositionModel): string {
    if (!pos.providedFrom && !pos.providedTo) {
      return '-';
    }

    const providedFrom: string = pos.providedFrom ? this.datePipe.transform(pos.providedFrom, 'dd.MM.yyyy') : '-';
    const providedTo: string = pos.providedTo ? this.datePipe.transform(pos.providedTo, 'dd.MM.yyyy') : '-';

    return `${providedFrom}-${providedTo}`;
  }

  public changePosition({position}): void {
    this.position = { ...this.preparePosition(position) };
    this.setupPositionsAndGroups();
    if (this.position.mediaType === PositionMediaTypeEnum.TEXT) {
      this.positionMediaFormComponentRef.resetTextEditorValue(this.position.description);
    }
    this.getCurrentRowIndex();
    this.getPositionsCards();
    this.getProductUnits();
    this.redrawControls();
  }

  public getCurrentRowIndex(): void {
    this.rowIndex = this.groupPositions
      .findIndex(pos => pos.order === this.position.order && pos.positionId === this.position.positionId);
  }

  // get hasLinkedSO(): boolean {
  //   return !!get(this, 'currentInvoice.linkedDocuments', [])
  //     .filter(doc => doc.type === DocumentTypesUppercaseEnum.SO).length;
  // }

  get isBundleGoods(): boolean {
    return this.position.productTypeForGrouping === ProductTypes.BUNDLE
      && this.position.bundleType === ProductTypes.GOODS;
  }

  get isBundleServices(): boolean {
    return this.position.productTypeForGrouping === ProductTypes.BUNDLE
      && this.position.bundleType === ProductTypes.SERVICES;
  }

  get isBundleDigital(): boolean {
    return this.position.productTypeForGrouping === ProductTypes.BUNDLE
      && this.position.bundleType === ProductTypes.DIGITAL;
  }

  get isCorrectionType(): boolean {
    return this.position.positionType.toLowerCase() === ProductTypes.CORRECTION;
  }

  get isGoodsProductType(): boolean {
    return this.position.productTypeForGrouping === ProductTypes.GOODS;
  }

  get isServicesProductType(): boolean {
    return this.position.productTypeForGrouping === ProductTypes.SERVICES;
  }

  get isDigitalProductType(): boolean {
    return this.position.productTypeForGrouping === ProductTypes.DIGITAL;
  }

  get isArbitraryProductType(): boolean {
    return this.position.productTypeForGrouping === ProductTypes.ARBITRARY;
  }
  get isArbitraryGoods(): boolean {
    return this.position.productTypeForGrouping === ProductTypes.ARBITRARY
      && this.position.productType === ProductTypes.GOODS;
  }
  get isArbitraryServices(): boolean {
    return this.position.productTypeForGrouping === ProductTypes.ARBITRARY
      && this.position.productType === ProductTypes.SERVICES;
  }
  get isArbitraryDigital(): boolean {
    return this.position.productTypeForGrouping === ProductTypes.ARBITRARY
      && this.position.productType === ProductTypes.DIGITAL;
  }

  get isBundleProductType(): boolean {
    return this.position.productTypeForGrouping === ProductTypes.BUNDLE;
  }

  get allowFractionalValues(): boolean {
    if (!this.position.unitType || !this.unitsListSource.length) { return true; }
    const selectedUnit: ProductUnitModel = this.unitsListSource
      .find((itm: ProductUnitModel) => itm.name === this.position.unitType);
    const isFractionalQuantity: boolean = !!(this.position.quantity % 1);
    return (selectedUnit && !isFractionalQuantity)
      ? selectedUnit.allowFractionalValues
      : isFractionalQuantity;
  }

  get isGoods(): boolean {
    return this.position.productType === ProductTypes.GOODS && !this.position.general;
  }

  get isOverdueFeePosition(): boolean {
    return this.position.productType === ProductTypes.OVERDUE_FEE;
  }

  get isPrepaymentPosition(): boolean {
    return this.position.productType === ProductTypes.PREPAYMENT;
  }

  get isFinalInvoice(): boolean {
    return this.data.currentInvoice.type === OinTypeEnum.FINAL;
  }

  get isChannelOffers(): boolean {
    return this.data.currentInvoice.channel === 'OFFERS';
  }

  get isRegularInvoice(): boolean {
    return this.data.currentInvoice.type === OinTypeEnum.REGULAR;
  }

  get isInvoicePosition(): boolean {
    return this.position.productType === ProductTypes.INVOICE;
  }

  get isCreditNotePosition(): boolean {
    return this.position.productType === ProductTypes.CREDIT_NOTE;
  }
  get isCreditPosition(): boolean {
    return this.position.productType === ProductTypes.CREDIT;
  }

  get priceOptionNet(): boolean {
    return this.data.currentInvoice.preferences.priceOption === PriceOptionEnum.NET
      || this.data.currentInvoice.billingData.smallBusiness;
  }
  get priceOptionGross(): boolean {
    return this.data.currentInvoice.preferences.priceOption === PriceOptionEnum.GROSS
      && !this.data.currentInvoice.billingData.smallBusiness;
  }

  get deletable(): boolean {
    return !this.isFinalInvoice ||
      !!this.position.mediaType ||
      (this.isChannelOffers &&
        !this.position.oinPositionId &&
        (
          this.position.productTypeForGrouping === ProductTypes.DIGITAL ||
          this.position.productTypeForGrouping === ProductTypes.SERVICES ||
          this.position.productTypeForGrouping === ProductTypes.GOODS
        )
      );
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

}
